import type { SetSessionConfigOptionResponse } from "@agentclientprotocol/sdk";
import { AcpClient } from "../../acp/client.js";
import {
  assertControlAuthority,
  withInterrupt,
  type AcpControlAuthority,
} from "../../async-control.js";
import { applyConfigOptionsToRecord } from "../../session/config-options.js";
import { advertisedModelState } from "../../session/model-state.js";
import { absolutePath, isoNow } from "../../session/persistence.js";
import type {
  AcpPermissionHandler,
  AcpElicitationMode,
  AuthPolicy,
  McpServer,
  NonInteractivePermissionPolicy,
  PermissionMode,
  PermissionPolicy,
  SessionRecord,
  SessionResumePolicy,
} from "../../types.js";
import { applyLifecycleSnapshotToRecord } from "./lifecycle.js";
import {
  connectAndLoadSession,
  type ConnectAndLoadSessionOptions,
  type ConnectedSessionController,
} from "./reconnect.js";
import { sessionOptionsFromRecord } from "./session-options.js";

export type FullConnectedSessionController = ConnectedSessionController & {
  setSessionModel: (modelId: string) => Promise<SetSessionConfigOptionResponse | undefined>;
  setSessionConfigOption: (
    configId: string,
    value: string,
  ) => Promise<SetSessionConfigOptionResponse>;
};

type ConnectedSessionContext = {
  record: SessionRecord;
  client: AcpClient;
  activeController: FullConnectedSessionController;
  sessionId: string;
  resumed: boolean;
  loadError?: string;
};

export type WithConnectedSessionOptions<T> = {
  sessionRecordId: string;
  loadRecord: (sessionRecordId: string) => Promise<SessionRecord>;
  saveRecord: (record: SessionRecord) => Promise<void>;
  createClient?: (options: ConstructorParameters<typeof AcpClient>[0]) => AcpClient;
  mcpServers?: McpServer[];
  permissionMode?: PermissionMode;
  nonInteractivePermissions?: NonInteractivePermissionPolicy;
  permissionPolicy?: PermissionPolicy;
  onPermissionRequest?: AcpPermissionHandler;
  authCredentials?: Record<string, string>;
  authPolicy?: AuthPolicy;
  fs?: boolean;
  terminal?: boolean;
  elicitationModes?: readonly AcpElicitationMode[];
  resumePolicy?: SessionResumePolicy;
  replacingMode?: true;
  replacingConfigOption?: ConnectAndLoadSessionOptions["replacingConfigOption"];
  timeoutMs?: number;
  authority?: AcpControlAuthority;
  verbose?: boolean;
  onClientAvailable?: (controller: FullConnectedSessionController) => void;
  onClientClosed?: () => void;
  onConnectedRecord?: (record: SessionRecord) => void;
  onInterrupt?: (params: { client: AcpClient; record: SessionRecord }) => Promise<void>;
  run: (context: ConnectedSessionContext) => Promise<T>;
};

export type WithConnectedSessionResult<T> = {
  value: T;
  record: SessionRecord;
  resumed: boolean;
  loadError?: string;
};

function createActiveSessionController(params: {
  client: AcpClient;
  record: SessionRecord;
  getActiveSessionId: () => string;
}): FullConnectedSessionController {
  const getActiveSessionId = () => params.getActiveSessionId();
  return {
    hasActivePrompt: () => params.client.hasActivePrompt(),
    requestCancelActivePrompt: async () => await params.client.requestCancelActivePrompt(),
    setSessionMode: async (modeId: string) => {
      await params.client.setSessionMode(getActiveSessionId(), modeId);
    },
    setSessionModel: async (modelId: string) => {
      const models = advertisedModelState(params.record.acpx);
      const response = await params.client.setSessionModel(getActiveSessionId(), modelId, models);
      applyConfigOptionsToRecord(params.record, response);
      return response;
    },
    setSessionConfigOption: async (configId: string, value: string) => {
      return await params.client.setSessionConfigOption(
        getActiveSessionId(),
        configId,
        value,
        advertisedModelState(params.record.acpx),
      );
    },
  };
}

export async function withConnectedSession<T>(
  options: WithConnectedSessionOptions<T>,
): Promise<WithConnectedSessionResult<T>> {
  const record = await options.loadRecord(options.sessionRecordId);
  assertControlAuthority(options.authority);
  const clientOptions: ConstructorParameters<typeof AcpClient>[0] = {
    agentCommand: record.agentCommand,
    agentArgv: record.agentArgv,
    cwd: absolutePath(record.cwd),
    mcpServers: options.mcpServers,
    permissionMode: options.permissionMode ?? "approve-reads",
    nonInteractivePermissions: options.nonInteractivePermissions,
    permissionPolicy: options.permissionPolicy,
    onPermissionRequest: options.onPermissionRequest,
    authCredentials: options.authCredentials,
    authPolicy: options.authPolicy,
    fs: options.fs,
    terminal: options.terminal,
    elicitationModes: options.elicitationModes,
    verbose: options.verbose,
    sessionOptions: sessionOptionsFromRecord(record),
  };
  const client = options.createClient?.(clientOptions) ?? new AcpClient(clientOptions);
  let activeSessionIdForControl = record.acpSessionId;
  let notifiedClientAvailable = false;
  const activeController = createActiveSessionController({
    client,
    record,
    getActiveSessionId: () => activeSessionIdForControl,
  });

  try {
    return await withInterrupt(
      async () => {
        const { sessionId, resumed, loadError } = await connectAndLoadSession({
          client,
          record,
          resumePolicy: options.resumePolicy,
          replacingMode: options.replacingMode,
          replacingConfigOption: options.replacingConfigOption,
          authority: options.authority,
          timeoutMs: options.timeoutMs,
          verbose: options.verbose,
          activeController,
          onClientAvailable: (controller) => {
            options.onClientAvailable?.(controller);
            notifiedClientAvailable = true;
          },
          onConnectedRecord: options.onConnectedRecord,
          onSessionIdResolved: (sessionIdValue) => {
            activeSessionIdForControl = sessionIdValue;
          },
        });

        assertControlAuthority(options.authority);
        const value = await options.run({
          record,
          client,
          activeController,
          sessionId,
          resumed,
          loadError,
        });

        const now = isoNow();
        record.lastUsedAt = now;
        record.closed = false;
        record.closedAt = undefined;
        record.protocolVersion = client.initializeResult?.protocolVersion;
        record.agentCapabilities = client.initializeResult?.agentCapabilities;
        applyLifecycleSnapshotToRecord(record, client.getAgentLifecycleSnapshot());
        await options.saveRecord(record);

        return {
          value,
          record,
          resumed,
          loadError,
        };
      },
      async () => {
        if (options.onInterrupt) {
          await options.onInterrupt({ client, record });
        } else {
          await client.cancelActivePrompt(2_500);
        }
        applyLifecycleSnapshotToRecord(record, client.getAgentLifecycleSnapshot());
        record.lastUsedAt = isoNow();
        await options.saveRecord(record).catch(() => {
          // best effort while process is being interrupted
        });
        await client.close();
      },
    );
  } finally {
    if (notifiedClientAvailable) {
      options.onClientClosed?.();
    }
    await client.close();
    applyLifecycleSnapshotToRecord(record, client.getAgentLifecycleSnapshot());
    await options.saveRecord(record).catch(() => {
      // best effort on close
    });
  }
}
